import React, { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import Chip from "@mui/material/Chip";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import { LIST_TEMPLATES_ANALYTICS_ENDPOINT } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import { MenuItem } from "@mui/material";
import { Select } from "@mui/material";
import { Grid } from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Search from "@mui/icons-material/Search";
import Typography from "@mui/material/Typography";
import TableSearchInput from "../TableSearchInput";
import TableFilterButton from "../TableFilterButton";
import { FilterModal, FilterModalItem } from "../FilterModal";
import { Category, Translate, Campaign } from "@mui/icons-material";
import CustomTabs from "../../common/custom-tabs";
import AppTable from "../../common/app-table";
import AppDropdown from "../../common/app-dropdown";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import { grayFontColor } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import { loadFilters, saveFilters, FILTER_NAMESPACES } from "../../utils/filterStorage";
import { formatDateOnly, formatWithTzLabel } from "../../utils/dateUtils";

const columns = [
  { id: "template_name", label: "Template Name" },
  { id: "category", label: "Category" },
  { id: "language", label: "Language" },
  { id: "created_date", label: "Created Date" },
  { id: "last_broadcast_date", label: "Last Broadcast Date Time" },
  { id: "broadcast_type", label: "Broadcast Type" },
  { id: "broadcasted_by", label: "Broadcasted By" },
];

export default function TemplateAnalyticsTable({
  refreshKey,
  dateInput,
  setTemplatesNamesData,
  onTemplateClick,
}) {
  const { getValidToken } = useAuth();
  const [rowCount, setRowCount] = useState(0);
  const [templatesData, setTemplateData] = useState([]);
  const [languageList, setLanguageList] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [templatesModalState, settemplatesModalState] = useState({
    isOpen: false,
    selectedClient: null,
    mode: "add",
    jsonData: "",
  });
  const URL =
    process.env.NODE_ENV === "production" ? "api" : `http://localhost:8000/api`;
  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 20,
  });
  const [savedFilters] = useState(() => loadFilters(FILTER_NAMESPACES.TEMPLATE_ANALYTICS, {
    searchQuery: '',
    languageFilter: '',
    categoryFilter: '',
    broadcastTypeFilter: '',
    appliedDateRange: [null, null],
  }));
  const [searchQuery, setSearchQuery] = useState(savedFilters.searchQuery);
  const [charCount, setCharCount] = useState(savedFilters.searchQuery.length);

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState(savedFilters.appliedDateRange);
  const [appliedDateRange, setAppliedDateRange] = useState(savedFilters.appliedDateRange);
  const [dateError, setDateError] = useState(null);
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const captilazeFirstLetter = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const languageOptions = [...new Set(languageList)].map((language) => (
    <MenuItem key={language} value={language}>
      {captilazeFirstLetter(language)}
    </MenuItem>
  ));

  const categoryOptions = [
    ...new Set(templatesData.map((template) => template.category)),
  ].map((category) => (
    <MenuItem key={category} value={category}>
      {captilazeFirstLetter(category)}
    </MenuItem>
  ));

  const broadcastTypeOptions = ["CSV", "Segment", "Single", "Retry", "API", "Nodebot", "Not Used"];

  // initilized state variables to store data
  const [languageFilter, setLanguageFilter] = useState(savedFilters.languageFilter);
  const [categoryFilter, setCategoryFilter] = useState(savedFilters.categoryFilter);
  const [broadcastTypeFilter, setBroadcastTypeFilter] = useState(savedFilters.broadcastTypeFilter);

  useEffect(() => {
    saveFilters(FILTER_NAMESPACES.TEMPLATE_ANALYTICS, {
      searchQuery,
      languageFilter,
      categoryFilter,
      broadcastTypeFilter,
      appliedDateRange,
    });
  }, [searchQuery, languageFilter, categoryFilter, broadcastTypeFilter, appliedDateRange]);

  const fetchData = async () => {
    // Construct query parameters for server-side filtering and pagination
    const queryParams = new URLSearchParams({
      limit: controller.rowsPerPage?.toString(),
      offset: (controller.serverSidePage * controller.rowsPerPage).toString(),
      search_query: searchQuery,
      language_filter: languageFilter,
      category_filter: categoryFilter,
      broadcast_type_filter: broadcastTypeFilter,
    });
    if (appliedDateRange[0]) {
      queryParams.append(
        "from_date",
        dayjs(appliedDateRange[0]).format("YYYY-MM-DD"),
      );
    }
    if (appliedDateRange[1]) {
      queryParams.append(
        "to_date",
        dayjs(appliedDateRange[1]).format("YYYY-MM-DD"),
      );
    }

    const token = await getValidToken();

    const tenant = localStorage.getItem("tenant");

    try {
      const response = await fetch(
        `${LIST_TEMPLATES_ANALYTICS_ENDPOINT}?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const jsonData = await response.json();

      console.log(jsonData);

      setTemplateData(jsonData.results);
      setLanguageList(jsonData.language_list);
      setTemplatesNamesData(jsonData.template_names);
      setRowCount(jsonData.count);
    } catch (error) {
      console.error("Error fetching data:", error.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    controller.serverSidePage,
    controller.rowsPerPage,
    refreshKey,
    dateInput,
    searchQuery,
    languageFilter,
    categoryFilter,
    broadcastTypeFilter,
    appliedDateRange,
  ]);

  // pagination
  const handlePageChange = (event, newPage) => {
    setController((prev) => ({
      ...prev,
      page: newPage,
      serverSidePage: newPage, // Sync server-side page with UI page
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    setController({
      ...controller,
      rowsPerPage: parseInt(event.target.value, 20),
      page: 0,
    });
  };

  const handleSearchQueryChange = (event) => {
    if (event.target.value.length > 50) {
      return;
    }
    const value = event.target.value;
    setSearchQuery(event.target.value);
    setCharCount(value.length);
  };

  const handleToDateOpen = () => {
    if (dateRange[0]) setEndDatePickerOpen(true);
  };

  const handleDateRangeCancel = () => {
    setDateRange([null, null]);
    setAppliedDateRange([null, null]);
    setDateError(null);
    setController((prev) => ({ ...prev, page: 0, serverSidePage: 0 }));
  };

  const handleDateRangeApply = () => {
    if (dateRange[0] && dateRange[1]) {
      setAppliedDateRange([dateRange[0], dateRange[1]]);
      setController((prev) => ({ ...prev, page: 0, serverSidePage: 0 }));
    }
  };

  const handleTemplateNameClick = (template, e) => {
    if (!template.active) {
      e.preventDefault();
      return; // Prevent interactions with disabled templates
    }

    // Call the parent component's handler to show analytics
    if (onTemplateClick) {
      onTemplateClick(template);
    }

    // Optionally keep the preview functionality
    if (template.template_preview_json !== "{}") {
      console.log("template json present");
      settemplatesModalState({
        isOpen: true,
        selectedClient: template,
        mode: "view",
        jsonData: template.template_preview_json,
      });
    } else {
      console.error("No JSON found for template");
    }
  };

  const handleLanguageFilterChange = (event) => {
    setLanguageFilter(event.target.value);
  };

  const handleCategoryFilterChange = (event) => {
    setCategoryFilter(event.target.value);
  };

  const handleBroadcastTypeFilterChange = (event) => {
    setBroadcastTypeFilter(event.target.value);
  };

  const handleRefresh = () => {
    setController({
      page: 0,
      rowsPerPage: 20,
    });
    fetchData();
  };

  const getTotalActiveFilters = () => {
    let count = 0;
    if (languageFilter) count++;
    if (categoryFilter) count++;
    if (broadcastTypeFilter) count++;
    return count;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const resetAllFilters = () => {
    setLanguageFilter("");
    setCategoryFilter("");
    setBroadcastTypeFilter("");
    handleDateRangeCancel();
    setSearchQuery("")
  };
  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          mb: 2,
          py: 1.5,
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          <TableSearchInput
            searchQuery={searchQuery}
            onSearchChange={handleSearchQueryChange}
            charCount={charCount}
            placeholder="Search by Template Name"
          />
          <TableFilterButton
            setFilterModalOpen={setFilterModalOpen}
            totalActiveFilters={getTotalActiveFilters()}
          />
        </Box>
      </Box>

      <Paper
        sx={{ width: "100%", overflow: "visible", mt: 2, boxShadow: "none" }}
      >
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            py: 1.5,
          }}
        >
          {/* Search Field */}
          <Grid
            item
            sm={6}
            md={4}
            lg={3}
            sx={{ display: { xs: "none", sm: "inherit" }, width: "300px" }}
          >
            <TableSearchInput
              searchQuery={searchQuery}
              onSearchChange={handleSearchQueryChange}
              charCount={charCount}
              placeholder="Search by Template Name"
            />
          </Grid>
          <Box>
            <AppDropdown
              endIcon={<KeyboardArrowDownOutlinedIcon sx={{ color: grayFontColor }} />}
              menuItems={[...new Set(languageList)].map((lang) => ({
                label: captilazeFirstLetter(lang),
                value: lang,
              }))}
              selectedValue={languageFilter}
              onMenuSelect={(value) => setLanguageFilter(value)}
            >
              {languageFilter
                ? captilazeFirstLetter(languageFilter)
                : "Language"}
            </AppDropdown>
          </Box>
          <Box>
            <AppDropdown
              endIcon={<KeyboardArrowDownOutlinedIcon sx={{ color: grayFontColor }} />}
              menuItems={[
                { label: "Marketing", value: "marketing" },
                { label: "Utility", value: "utility" },
              ]}
              selectedValue={categoryFilter}
              onMenuSelect={(value) => setCategoryFilter(value)}
            >
              {categoryFilter
                ? captilazeFirstLetter(categoryFilter)
                : "Category"}
            </AppDropdown>
          </Box>
          <Box>
            <AppDropdown
              endIcon={<KeyboardArrowDownOutlinedIcon sx={{ color: grayFontColor }} />}
              menuItems={broadcastTypeOptions.map((type) => ({
                label: type,
                value: type,
              }))}
              selectedValue={broadcastTypeFilter}
              onMenuSelect={(value) => setBroadcastTypeFilter(value)}
            >
              {broadcastTypeFilter || "Broadcast Type"}
            </AppDropdown>
          </Box>
          <Box>
            <AppDropdown
              endIcon={
                <KeyboardArrowDownOutlinedIcon sx={{ color: grayFontColor }} />
              }
              paperProps={{
                width: dateAnchorEl ? dateAnchorEl.offsetWidth * 2 : undefined,
                maxWidth: "500px",
              }}
              menuContent={(closeMenu) => (
                <Box sx={{ p: 2 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <Typography sx={{ ...typography.bodyLarge }}>
                         Select date range:
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <DatePicker
                          format="DD/MM/YYYY"
                          disableFuture
                          label="Start Date"
                          value={dateRange[0]}
                          onChange={(newValue) => {
                            setDateRange([newValue, dateRange[1]]);
                            setController((prev) => ({
                              ...prev,
                              page: 0,
                              serverSidePage: 0,
                            }));
                          }}
                          slotProps={{
                            textField: {
                              size: "small",
                              sx: {
                                width: "200px",
                                "& .MuiInputBase-root": { height: "40px" },
                              },
                            },
                          }}
                        />
                        <DatePicker
                          format="DD/MM/YYYY"
                          disableFuture
                          label="End Date"
                          value={dateRange[1]}
                          onChange={(newValue) => {
                            setDateRange([dateRange[0], newValue]);
                            setController((prev) => ({
                              ...prev,
                              page: 0,
                              serverSidePage: 0,
                            }));
                          }}
                          minDate={dateRange[0] || undefined}
                          open={endDatePickerOpen}
                          onOpen={handleToDateOpen}
                          onClose={() => setEndDatePickerOpen(false)}
                          slotProps={{
                            textField: {
                              error:
                                (dateRange[1] && !dateRange[0]) || dateError,
                              helperText:
                                dateRange[1] && !dateRange[0]
                                  ? "Please select Start Date first"
                                  : dateError,
                              size: "small",
                              sx: {
                                width: "200px",
                                "& .MuiInputBase-root": { height: "40px" },
                              },
                            },
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 1,
                        }}
                      >
                        <ButtonTwo
                          onClick={() => {
                            handleDateRangeCancel();
                            closeMenu();
                          }}
                          variant="outlined"
                        >
                          Cancel
                        </ButtonTwo>
                        <ButtonOne
                          onClick={() => {
                            handleDateRangeApply();
                            closeMenu();
                          }}
                          variant="contained"
                          disabled={!dateRange[0] || !dateRange[1]}
                        >
                          Apply
                        </ButtonOne>
                      </Box>
                    </Box>
                  </LocalizationProvider>
                </Box>
              )}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    mr: appliedDateRange[0] && appliedDateRange[1] ? 1 : 0,
                    flexShrink: 0,
                  }}
                >
                  {appliedDateRange[0] && appliedDateRange[1]
                    ? "Created Date:"
                    : "Created Date"}
                </Box>
                {appliedDateRange[0] && appliedDateRange[1] && (
                  <Chip
                    size="small"
                    onDelete={handleDateRangeCancel}
                    label={`${appliedDateRange[0].format("DD/MM/YYYY")} - ${appliedDateRange[1].format("DD/MM/YYYY")}`}
                    sx={{
                      maxWidth: { sm: 80, md: 200 },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  />
                )}
              </Box>
            </AppDropdown>
          </Box>
          <Box>
            <ButtonTwo
              title="Reset Filters"
              onClick={resetAllFilters}
              sx={{ height: 40 }}
            />
          </Box>
        </Box>

        {/* Sync button */}
        {/* <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        p: 1,
                        m: 1,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                    }}
                    >
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button variant="contained" onClick={handleRefresh}>
                            <RefreshIcon sx={{ mr: 1 }} /> Sync
                        </Button>
                    </Grid>
                </Box> */}

        <AppTable
          columns={[
            {
              id: "template_name",
              label: "Template Name",
              render: (template) =>
                template.active === false ? (
                  <Tooltip title="This template has been disabled">
                    <span style={{ color: "#9e9e9e" }}>
                      {template.template_name}
                    </span>
                  </Tooltip>
                ) : (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleTemplateNameClick(template, e);
                    }}
                  >
                    {template.template_name}
                  </a>
                ),
            },
            {
              id: "category",
              label: "Category",
              render: (t) =>
                t.category === "MARKETING"
                  ? "Marketing"
                  : t.category === "UTILITY"
                    ? "Utility"
                    : "Unknown",
            },
            {
              id: "language",
              label: "Language",
              render: (t) => captilazeFirstLetter(t.language),
            },
            {
              id: "created_at",
              label: "Created Date",
              render: (t) => formatDateOnly(t.created_at_iso),
            },
            {
              id: "last_broadcast_datetime",
              label: "Last Broadcast Date Time",
              render: (t) => formatWithTzLabel(t.last_broadcast_datetime_iso),
            },
            { id: "broadcast_type", label: "Broadcast Type" },
            { id: "last_broadcasted_by", label: "Broadcasted By" },
          ]}
          rows={templatesData}
          rowKey={(t, i) => i}
          emptyMessage="No match found"
          maxHeight={530}
          containerSx={{
            scrollbarWidth: "thin",
            scrollbarColor: "#C5CEDB #F3F6FB",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": { backgroundColor: "#F3F6FB", borderRadius: "8px" },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#C5CEDB", borderRadius: "8px" },
            "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#A8B4C7" },
          }}
          rowSx={(template) =>
            template.active === false
              ? {
                  backgroundColor: "#f5f5f5",
                  opacity: 0.7,
                  cursor: "not-allowed",
                  "&:hover": { backgroundColor: "#f5f5f5" },
                }
              : {}
          }
          count={rowCount}
          page={controller.page}
          rowsPerPage={controller.rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onReset={resetAllFilters}
      >
        <FilterModalItem
          name="Language"
          data={[...new Set(languageList)]}
          value={languageFilter}
          onChange={handleLanguageFilterChange}
          icon={<Translate />}
        />
        <FilterModalItem
          name="Category"
          data={["marketing", "utility"]}
          value={categoryFilter}
          onChange={handleCategoryFilterChange}
          icon={<Category />}
        />
        <FilterModalItem
          name="Broadcast Type"
          data={broadcastTypeOptions}
          value={broadcastTypeFilter}
          onChange={handleBroadcastTypeFilterChange}
          icon={<Campaign />}
          renderItem={(item) => (
            <MenuItem key={item} value={item}>
              {item}
            </MenuItem>
          )}
        />
      </FilterModal>
    </Box>
  );
}
